/**
 * The panel shows the one assistant everywhere; its focus follows the route:
 * a client page (`/clients/:id`, every tab) gets the assistant at that
 * client's focus,
 * a case page gets no panel (the assistant lives in the IDE there), an agent
 * workspace page or the onboarding page gets no panel (the page is the
 * agent), and everything else
 * gets the assistant at the practice focus. The practice scope comes from
 * the agent catalog.
 */
import { useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";

import { parseCasePath } from "../caseIde/routes";
import { isOnboardingPath } from "../onboarding/routes";
import type { PanelAgent } from "./agentPanelStore";
import type { AgentScope } from "./types";
import { useAgentSpecs } from "./useAgentSpecs";

/** The client a client-profile path is about, or null (case paths included). */
export const clientIdFromPath = (pathname: string): string | null => {
  if (parseCasePath(pathname)) return null;
  const profile = matchPath({ path: "/clients/:clientId/*" }, pathname);
  const id = (profile?.params.clientId ?? "").trim();
  return id && id !== "new" ? id : null;
};

/**
 * The panel's focus for a path, or null where the panel is not offered
 * (cases, the agents' own workspace pages and onboarding).
 */
export const panelAgentForPath = (pathname: string): PanelAgent | null => {
  if (parseCasePath(pathname) || isOnboardingPath(pathname)) return null;
  return clientIdFromPath(pathname) ? "client" : "practice";
};

export type AgentPanelScopes = {
  /** The panel's focus; null on case routes, where the panel is hidden. */
  agent: PanelAgent | null;
  /** The scope the agent works in; null while the catalog loads or without a practice. */
  scope: AgentScope | null;
  loading: boolean;
  error: string | null;
};

export const useAgentPanelScope = (): AgentPanelScopes => {
  const { pathname } = useLocation();
  const agent = useMemo(() => panelAgentForPath(pathname), [pathname]);
  const clientId = useMemo(() => clientIdFromPath(pathname), [pathname]);
  const { practiceScope, loading, error } = useAgentSpecs();

  const scope = useMemo<AgentScope | null>(() => {
    if (agent === "client")
      return clientId ? { kind: "client", id: clientId } : null;
    if (agent === "practice") return practiceScope;
    return null;
  }, [agent, clientId, practiceScope]);

  return { agent, scope, loading, error };
};
